import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, IconButton, Image, Text } from '@chakra-ui/react';
import { PageHeader as ThemePageHeader } from '@themes/aegix';
import { HiUser } from 'react-icons/hi2';
import { useSearch } from '../../contexts/SearchContext';
import { useUser } from '@aegix/auth-identity';
import type { SMSUser } from '../../lib/sms-auth';
import { ProfileDrawer } from '../ProfileDrawer';

type ThemePageHeaderProps = React.ComponentProps<typeof ThemePageHeader>;

const SCREEN_READER_KEY = 'aegix_sms_screen_reader';

function readInitialScreenReader(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SCREEN_READER_KEY) === 'true';
  } catch {
    return false;
  }
}

interface CurrentUserAvatarProps {
  name: string;
  avatar?: string;
}

/**
 * Header avatar button that opens the SMS ProfileDrawer directly.
 *
 * Renders a small avatar (image if provided, falls back to the HiUser icon)
 * and emits `onClick`. Mirrors the theme PageHeader's built-in user icon so
 * the visual is consistent, but skips the theme's intermediate "Account"
 * drawer.
 */
function ProfileButton({
  user,
  onClick,
}: {
  user: CurrentUserAvatarProps;
  onClick: () => void;
}) {
  return (
    <IconButton
      aria-label={`Open profile for ${user.name}`}
      bg="button.icon"
      color="text.primary"
      borderRadius="8px"
      size="md"
      _hover={{ bg: 'surface.tertiary' }}
      onClick={onClick}
      data-e2e="currentUser"
    >
      {user.avatar ? (
        <Image
          src={user.avatar}
          alt={user.name}
          borderRadius="full"
          w="20px"
          h="20px"
          objectFit="cover"
        />
      ) : (
        <HiUser size={20} />
      )}
    </IconButton>
  );
}

export function SMSPageHeader(props: ThemePageHeaderProps) {
  const { t } = useTranslation();
  const { openSearch } = useSearch();
  const user = useUser<SMSUser>();
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [screenReaderEnabled, setScreenReaderEnabled] = useState<boolean>(
    readInitialScreenReader,
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SCREEN_READER_KEY,
        screenReaderEnabled ? 'true' : 'false',
      );
    } catch {
      // localStorage unavailable (private mode / quota) — ignore
    }
  }, [screenReaderEnabled]);

  const currentUser: CurrentUserAvatarProps = user
    ? { name: `${user.first_name} ${user.last_name}`.trim() || user.email }
    : { name: 'User' };

  // Bypass the theme's two-layer user-menu drawer. We render our own
  // profile-icon IconButton in the `actions` slot and open ProfileDrawer
  // directly — one click instead of two-plus-nested-drawer drill-down.
  const mergedActions = (
    <HStack gap={2}>
      {props.actions}
      <ProfileButton
        user={currentUser}
        onClick={() => setProfileDrawerOpen(true)}
      />
    </HStack>
  );

  return (
    <>
      <ThemePageHeader
        showOptionsDrawer
        screenReaderEnabled={screenReaderEnabled}
        onScreenReaderToggle={setScreenReaderEnabled}
        optionsDrawerTitle={t('layout.options', 'Options')}
        notificationsDrawerTitle={t('layout.notifications', 'Notifications')}
        themeLabel={t('layout.theme', 'Theme')}
        screenReaderTitle={t('layout.screenReader', 'Screen Reader')}
        notificationsDrawerContent={
          <Box>
            <Text color="text.primary" fontSize="16px" fontWeight={500}>
              {t('notifications.noNewNotifications', 'No new notifications')}
            </Text>
            <Text color="text.secondary" fontSize="14px" mt={2}>
              {t(
                'notifications.seeAlertsNotice',
                "You'll see alerts and updates here when they arrive.",
              )}
            </Text>
          </Box>
        }
        {...props}
        actions={mergedActions}
        onSearchClick={() => {
          if (props.onSearchClick) {
            props.onSearchClick();
          } else {
            openSearch();
          }
        }}
      />
      <ProfileDrawer
        isOpen={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
      />
    </>
  );
}
